import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import SiteCard from '../../components/SiteCard';
import SEOComponent from '../../components/SEOComponent';

const HPIRevisions = ({ url }) => {
  return (
    <>
      <SEOComponent
        title={'Why the ONS HPI keeps getting revised down'}
        description={'New-build sales register slowly at HM Land Registry, and the ONS fills the gaps with a linear hedonic model. The combination produces systematic downward revisions in the official UK house price index. Here is what is going on, with sources.'}
        url={url}
        imageUrl={'https://ottaproperty.co.uk/blog_images/hpi_revisions/newbuild_registration_lag.png'}
      />
      <Container>
        <SiteCard
          header={'ottaproperty.co.uk blog'}
          title={'Why the ONS HPI keeps getting revised down'}
          content={
            <Container>
              <Row>
                <Col md={10} style={{ margin: 'auto' }}>

                  <p>
                    Every month the ONS publishes the UK House Price Index. And every month they revise last month, and the month before, and the month before that. Thirteen months of revisions, every release. The revisions almost always go the same way: down.
                  </p>

                  <p>
                    It's been bugging me for a while, so I went and looked. Here's what I think is going on.
                  </p>

                  <h2>The data takes ages to arrive</h2>
                  <hr />
                  <p>
                    When you buy a second-hand house, the sale gets recorded by HM Land Registry pretty quickly. There's already a title for the property. They just update who owns it.
                  </p>
                  <p>
                    New-builds are different. There's no title yet, so the registry has to make one from scratch. That means checking the plans, the boundaries, the freehold structure on the development, the leasehold terms on the flats, and so on. Each one takes a while. From the data I have, new-build sales typically take 12 to 18 months to land in the published files. The ONS themselves say <a href="https://uksa.statisticsauthority.gov.uk/wp-content/uploads/2025/03/APCP-T2501-HPI-Imputation.pdf" target="_blank" rel="noreferrer">"processing new build sales and creating a new record for a new property takes longer than updating an existing property record"</a>.
                  </p>
                  <p>
                    You can see it directly. I pulled the registry data, counted new-build sales by month, and got this:
                  </p>

                  <img
                    src="/blog_images/hpi_revisions/newbuild_registration_lag.png"
                    alt="Monthly count of new-build sales registered at HM Land Registry, with a sharp fall-off in recent months"
                    style={{ width: '100%' }}
                  />

                  <p>
                    Top panel is the monthly count of new-build sales. Five to fifteen thousand a month for a decade, tailing off through 2025 to almost nothing by early 2026. Bottom panel is the same as a share of all sales: 10 to 15 percent for ten years, then zero.
                  </p>
                  <p>
                    The right-hand edge is the lag. Sales from late 2024 haven't all registered yet, and the share will fill back in over the next year or two. So at first print of any recent HPI month, essentially all the new-builds are missing.
                  </p>

                  <h2>The HPI has to publish before the data is in</h2>
                  <hr />
                  <p>
                    The HPI comes out about six weeks after the month it covers. By that point, only a slice of the eventual transactions have been registered, and new-builds are barely there. So the ONS has a real problem. How do you fit a model on October when you've got a third of October's transactions, and the missing two thirds aren't a random sample?
                  </p>
                  <p>
                    The way the HPI works: every April, ONS builds a fixed basket of properties from the previous year's transactions. That's the prediction target, and missing values in it are filled in with a nearest-neighbour routine called CANCEIS. Every month, they fit a <a href="https://www.gov.uk/government/publications/about-the-uk-house-price-index/quality-and-methodology" target="_blank" rel="noreferrer">hedonic regression on that month's registered transactions</a>, then apply the fitted model to the basket to produce the index level.
                  </p>
                  <p>
                    Reasonable enough in principle. The bug is in the detail.
                  </p>

                  <h2>The actual bug</h2>
                  <hr />
                  <p>
                    The ONS published a paper in January 2025 explaining the mechanism. <a href="https://uksa.statisticsauthority.gov.uk/wp-content/uploads/2025/03/APCP-T2501-HPI-Imputation.pdf" target="_blank" rel="noreferrer">APCP-T2501</a>. The short version: the bias comes from missing floor area being handled differently in two parts of the same pipeline.
                  </p>
                  <p>
                    In the basket, missing floor area is imputed to a sensible non-zero value via CANCEIS. In the monthly training data, missing floor area is set to zero. Just zero. No imputation.
                  </p>
                  <p>
                    Now think about what happens when a new-build sale registers without a floor area populated yet (which is most of them, in the first few months after sale). The model sees a high-priced property with floor_area = 0. It has to explain the price. It can't put it on floor area because floor area is zero. So it loads the price onto the new-build flag coefficient. The coefficient inflates.
                  </p>
                  <p>
                    Then the fitted model gets applied to the basket. Every new-build in the basket has a real (imputed) floor area, so it gets a floor area contribution. It also gets the inflated new-build coefficient on top. Both contributions get added. New-build predicted price comes out too high. As the property attribute data fills in over the next thirteen months and the missingness rate drops, the inflated coefficient comes back down to where it should have been, and the index revises downward.
                  </p>
                  <p>
                    The lag determines how much missingness there is in the latest month. The asymmetric handling of that missingness turns it into systematic over-estimation. <a href="https://blog.ons.gov.uk/2025/08/20/home-improvement-how-we-are-developing-our-house-price-statistics/" target="_blank" rel="noreferrer">The August 2025 patch</a> fixes the asymmetry by imputing non-zero floor area in the monthly data too, so the train and basket distributions match. The temporary "pooling" workaround they'd been using since 2020 was removed completely from the 22 October 2025 release.
                  </p>

                  <h2>Why the linear model still matters</h2>
                  <hr />
                  <p>
                    The patch closes the asymmetry. It doesn't fix the deeper problem, which is that ONS runs a single Great Britain regression with a single new-build coefficient. A new-build flat in Newcastle has nothing in common with a new-build flat in Knightsbridge, but the linear model treats the premium as a country-wide constant.
                  </p>
                  <p>
                    Even with corrected imputation, that one coefficient is still doing all the work for newness across the entire country. When the missing-data composition shifts from one month to the next, even slightly, the GB-wide coefficient moves with it, and every cell in the basket inherits the move. The fragility lives in the linear hedonic itself, so the August imputation fix doesn't reach it. It removes the worst symptom; the structural sensitivity stays.
                  </p>

                  <h2>What it means if you read the numbers</h2>
                  <hr />
                  <p>
                    The provisional HPI for the most recent two or three months is a working estimate. The downward revisions are systematic, and they've been worse through 2024 and 2025 as the registration backlog has lengthened.
                  </p>
                  <p>
                    And if you're running a house price index in 2026, the real question is whether you should be imputing at all.
                  </p>

                  <h2>Sources</h2>
                  <hr />
                  <ul>
                    <li><a href="https://uksa.statisticsauthority.gov.uk/wp-content/uploads/2025/03/APCP-T2501-HPI-Imputation.pdf" target="_blank" rel="noreferrer">UK House Price Index monthly imputation methods</a>, UK Statistics Authority technical paper APCP-T2501, January 2025. The paper that diagnoses the train/basket asymmetry and proposes the fix.</li>
                    <li><a href="https://blog.ons.gov.uk/2025/08/20/home-improvement-how-we-are-developing-our-house-price-statistics/" target="_blank" rel="noreferrer">Home improvement: how we are developing our house price statistics</a>, ONS National Statistical blog, 20 August 2025. The deployment announcement.</li>
                    <li><a href="https://www.gov.uk/government/publications/about-the-uk-house-price-index/quality-and-methodology" target="_blank" rel="noreferrer">Quality and methodology, UK House Price Index</a>. The HPI feature set and regression structure.</li>
                  </ul>

                </Col>
              </Row>
            </Container>
          }
        />
      </Container>
    </>
  );
};

export default HPIRevisions;
